import React from 'react'
import { Well, EveryNSchedule, EveryNScheduleType } from '@pushly/aqe/lib/components'
import '@ant-design/compatible/assets/index.css'
import { Radio, Select, Tooltip } from 'antd'
import { WarningOutlined } from '@ant-design/icons'
import moment from 'moment-timezone'
import { IDayPartValue, DayPartSelect, IEveryNScheduleValue } from '@pushly/aqe/lib/components'
import { ISchedulingDatetimePickerValue, SchedulingDatetimePicker } from '../../../../components/scheduling-datetime-picker/scheduling-datetime-picker'

import { RecurrenceSchedule, DayOrdinal, MonthlyRelativeType } from '../../../../models/campaign/recurrence-schedule'
import { ALL_SCHEDULE_DAYS } from '@pushly/aqe/lib/components'

export interface IRecurrenceSectionProps {
  schedule: RecurrenceSchedule
  editable: boolean
  domain: any
  campaignTimeZone: string
  onUpdateSchedule: (mutate: (s: RecurrenceSchedule) => void) => void
}

import { buildAllowedDayMap } from '../../utils/build-allowed-day-map'

export const RecurrenceSection: React.FC<IRecurrenceSectionProps> = React.memo(({ schedule, editable, domain, campaignTimeZone, onUpdateSchedule }) => {
  // derive view state from model
  const type = schedule.getType()
  const relativeType = schedule.getRelativeType()
  const scheduleType = (relativeType ?? type) as string
  const scheduleSuperType = ((type as unknown as string) || 'daily').replace(/_relative|_specific/, '')

  const scheduleValue = schedule.serialize()
  const specificDay = schedule.getSpecificDay() ?? 1
  const relativeDay = schedule.getRelativeDay()
  const relativeDayOrdinal = schedule.getRelativeDayOrdinal()
  const pickerTimeZone = schedule.getTimeZone()
  const specificTime = schedule.getSpecificTime()

  // handlers rewritten to mutate the model via onUpdateSchedule
  const handleScheduleTypeChange = (nextSuper: string) => {
    onUpdateSchedule((s) => {
      const currSuper = (s.getType() as unknown as string)?.replace(/_relative|_specific/, '')
      let nextType = nextSuper
      if (nextSuper !== 'monthly') {
        if (currSuper === 'monthly') {
          s.setFrequency(1)
        }
        s.setAllowedDays(nextSuper === 'daily' ? ALL_SCHEDULE_DAYS : [])
      } else {
        nextType = 'monthly_specific'
        s.setSpecificDay(1)
      }
      s.setType(nextType)
    })
  }

  const handleMonthlyTypeChange = (mt: 'monthly_specific' | MonthlyRelativeType) => {
    onUpdateSchedule((s) => {
      if (mt === 'monthly_specific') {
        s.setSpecificDay(1)
        s.setRelativeType(undefined)
        s.setType('monthly_specific')
      } else {
        s.setRelativeType(mt as MonthlyRelativeType)
        if (mt === 'relative_day') {
          s.setRelativeDayOrdinal(DayOrdinal.FIRST)
        }
        s.setType('monthly_relative')
      }
    })
  }

  const handleEveryNChange = (value: IEveryNScheduleValue | undefined) => {
    onUpdateSchedule((s) => {
      s.setFrequency(value?.frequency)
      s.setAllowedDays(value?.allowed_days)
    })
  }

  const handleSpecificDayChange = (day: number) => {
    onUpdateSchedule((s) => s.setSpecificDay(day))
  }

  const handleRelativeDayOrdinalChange = (ordinal: DayOrdinal) => {
    onUpdateSchedule((s) => s.setRelativeDayOrdinal(ordinal))
  }

  const handleRelativeDayValueChange = (days: IDayPartValue) => {
    const selected = Object.keys(days).filter((d) => Boolean((days as any)[d]))
    onUpdateSchedule((s) => s.setRelativeDay(selected.length > 0 ? (selected[0] as string) : undefined))
  }

  const handleTimeChange = (val: ISchedulingDatetimePickerValue) => {
    onUpdateSchedule((s) => {
      s.setTimeZone(val.timeZone)
      s.setSpecificTime(val.time?.format('HH:mm'))
    })
  }

  return (
    <div className="recurrence-schedule-builder">
      <Well mode="ghost" title="Schedule Type" hideFooter={true}>
        <div className="schedule-type-row">
          <div className="schedule-type-wrapper">
            <Select
              className="campaign-trigger-editor-rec-schedule-type-select"
              size="small"
              disabled={!editable}
              value={scheduleSuperType}
              onChange={handleScheduleTypeChange}
            >
              <Select.Option value="daily">Daily</Select.Option>
              <Select.Option value="weekly">Weekly</Select.Option>
              <Select.Option value="monthly">Monthly</Select.Option>
            </Select>
          </div>
          <div className="monthly-type-wrapper">
            {/^(monthly|first_day|last_day|relative_day)/.test(scheduleType) && scheduleSuperType === 'monthly' && (
              <Radio.Group
                size="small"
                className="campaign-trigger-editor-rec-schedule-monthly-type-select"
                disabled={!editable}
                value={scheduleType}
                onChange={(e) => handleMonthlyTypeChange(e.target.value)}
              >
                <Tooltip title="The campaign will run once a month on the chosen day number of the month.">
                  <Radio.Button value="monthly_specific">Specific Day</Radio.Button>
                </Tooltip>
                <Tooltip title="The campaign will run once a month on the first day of the month.">
                  <Radio.Button value="first_day">First Day</Radio.Button>
                </Tooltip>
                <Tooltip title="The campaign will run once a month on the last day of the month.">
                  <Radio.Button value="last_day">Last Day</Radio.Button>
                </Tooltip>
                <Tooltip title="The campaign will run once a month on the chosen relative date of the month.">
                  <Radio.Button value="relative_day">Relative Day</Radio.Button>
                </Tooltip>
              </Radio.Group>
            )}
          </div>
        </div>
      </Well>

      {scheduleSuperType !== 'monthly' && (
        <Well mode="ghost" title={`${scheduleSuperType === 'daily' ? 'Daily' : 'Weekly'} Schedule`} hideFooter={true}>
          <EveryNSchedule
            key={scheduleSuperType}
            type={scheduleSuperType as EveryNScheduleType}
            disabled={!editable}
            value={scheduleValue}
            onChange={handleEveryNChange}
          />
        </Well>
      )}

      {scheduleType === 'monthly_specific' && (
        <Well mode="ghost" title="Specific Day" hideFooter={true}>
          <Select
            className="specific-day-select"
            size="small"
            disabled={!editable}
            defaultValue={1}
            value={specificDay ?? 1}
            onChange={handleSpecificDayChange}
          >
            {Array.from({ length: 31 }).map((_, idx) => {
              const day = idx + 1
              return (
                <Select.Option key={day} value={day}>
                  {day}
                </Select.Option>
              )
            })}
          </Select>
          {(specificDay ?? 1) > 28 && (
            <div className="form-layout-row specific-day-warning">
              <div className="form-layout-row-sub">
                <WarningOutlined />
                <span>
                  This campaign will not run in months that do not have the chosen number of days. To always target the last day of the month use the “Relative Day” option.
                </span>
              </div>
            </div>
          )}
        </Well>
      )}

      {scheduleType === 'relative_day' && (
        <Well mode="ghost" title="Relative Day" hideFooter={true}>
          <div className="relative-day-wrapper">
            <Select
              className="relative-day-ordinal-select"
              size="small"
              disabled={!editable}
              value={relativeDayOrdinal}
              onChange={handleRelativeDayOrdinalChange}
            >
              <Select.Option value="first">First</Select.Option>
              <Select.Option value="second">Second</Select.Option>
              <Select.Option value="third">Third</Select.Option>
              <Select.Option value="fourth">Fourth</Select.Option>
              <Select.Option value="fifth">Fifth</Select.Option>
            </Select>

            <DayPartSelect
              className="relative-day-select"
              size="small"
              mode="single"
              disabled={!editable}
              daysOnly={true}
              dayFormat={'ddd'}
              weekBeginsOnSunday={true}
              defaultValue={buildAllowedDayMap(relativeDay ? [relativeDay] : [])}
              value={buildAllowedDayMap(relativeDay ? [relativeDay] : [])}
              onChange={handleRelativeDayValueChange}
            />
          </div>
        </Well>
      )}

      <Well mode="ghost" title="Schedule Time" hideFooter={true}>
        <SchedulingDatetimePicker
          size="small"
          mode="time"
          domain={domain}
          disabled={!editable}
          timeZone={campaignTimeZone}
          hideTimeZoneOptions={true}
          value={{
            timeZone: pickerTimeZone,
            time: !specificTime ? undefined : moment(specificTime, 'HH:mm'),
          }}
          onChange={handleTimeChange}
        />
      </Well>
    </div>
  )
})
